import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { AuthService } from '../service/auth/auth.service';
import { RestService } from '../service/rest/rest.service';
import { MessagesNotificationService } from '../service/messages-notification.service';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { OAuthService } from 'angular-oauth2-oidc';
import { RuntimeConfigService } from '../service/config/runtime-config.service';
import { oidcDiscoveryState } from '../oidc-discovery.state';
import { setupOidcDiscovery } from '../oidc-app.initializer';
import { LoggerService } from '../service/utils/logger.service';
import { isCapacitorNative } from '../service/platform/capacitor-platform';
import { PlatformService } from '../service/platform/platform.service';
import { LegalFooterComponent } from '../legal/legal-footer.component';
import { TERMS_VERSION, PRIVACY_VERSION } from '../legal/legal-content';
import { ConnectivityService } from '../service/connectivity/connectivity.service';
import { PolicyAcceptanceStorageService } from '../service/legal/policy-acceptance-storage.service';
import { isTermsEndpointMissing } from '../service/legal/policy-terms.util';
import { BrandService } from '../service/brand/brand.service';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterLink, LegalFooterComponent],
  templateUrl: './login.component.html',
  styleUrl: './login.component.css',
})
export class LoginComponent implements OnInit {
  emergencyFailed = false;
  emergencyForm: FormGroup;
  emailPasswordForm: FormGroup;
  emailPasswordError = '';
  emailPasswordLoading = false;
  /** Shown when user clicks browser OIDC while discovery never succeeded */
  oidcClickError = '';
  oidcDiscoveryRetrying = false;
  readonly oidcDiscovery = oidcDiscoveryState;
  passwordResetOk = false;
  signupConfirmed = false;

  /** Provider order follows store conventions: Apple-first on iOS (Apple App
   * Store guideline 4.8), Google-first on Android. Web stays Google-first. */
  protected readonly platform = inject(PlatformService);
  protected readonly brand = inject(BrandService);

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private restService: RestService,
    private messagesNotification: MessagesNotificationService,
    private route: ActivatedRoute,
    private router: Router,
    private oauth: OAuthService,
    public runtime: RuntimeConfigService,
    private connectivity: ConnectivityService,
    private policyStorage: PolicyAcceptanceStorageService,
  ) {
    this.emergencyForm = this.fb.group({
      password: ['', [Validators.required, Validators.minLength(4)]],
      username: ['', [Validators.required, Validators.minLength(4)]],
    });
    this.emailPasswordForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(6)]],
    });
  }

  ngOnInit(): void {
    if (this.route.snapshot.queryParamMap.get('reset') === 'ok') {
      this.passwordResetOk = true;
      void this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { reset: null },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    }
    if (this.route.snapshot.queryParamMap.get('signup') === 'confirmed') {
      this.signupConfirmed = true;
      void this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { signup: null },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    }
    void this.tryCompleteSupabaseRedirect();
    void this.maybeSkipLogin();
  }

  /**
   * If we already have (or can refresh) a valid session, skip the login screen.
   * Re-using the saved Supabase refresh token means returning users never see
   * the email/password form unless they explicitly logged out.
   */
  private async maybeSkipLogin(): Promise<void> {
    const ok = await this.authService.restoreSessionFromStorage();
    if (!ok) return;
    const user = this.authService.getUser();
    if (!user) return;
    LoggerService.log('[login] valid session restored from storage; skipping form');
    await this.navigateAfterLogin(user.sid, !!user.admin);
  }

  get canEmailPassword(): boolean {
    return !!(this.runtime.supabaseProjectUrl && this.runtime.supabaseApiKey);
  }

  async onEmailPasswordSubmit(): Promise<void> {
    this.emailPasswordError = '';
    if (!this.emailPasswordForm.valid) {
      this.emailPasswordForm.markAllAsTouched();
      return;
    }
    if (!this.canEmailPassword) {
      this.emailPasswordError =
        'Email sign-in needs Supabase URL and anon key in runtime-config (see runtime-config.example.json).';
      return;
    }
    this.emailPasswordLoading = true;
    try {
      const ok = await this.authService.signInWithEmailPassword(
        String(this.emailPasswordForm.value.email || '').trim(),
        String(this.emailPasswordForm.value.password || ''),
      );
      if (!ok) {
        this.emailPasswordError = 'Sign-in failed. Check email and password.';
        return;
      }
      const user = this.authService.getUser();
      await this.navigateAfterLogin(user?.sid ?? '', !!user?.admin);
    } finally {
      this.emailPasswordLoading = false;
    }
  }

  private async tryCompleteSupabaseRedirect(): Promise<void> {
    const win = window;
    const raw = win.location.hash?.replace(/^#/, '') || win.location.search?.replace(/^\?/, '') || '';
    if (!raw) return;
    const params = new URLSearchParams(raw);
    if (params.get('signup') === 'confirmed') {
      return;
    }
    const accessToken = params.get('access_token');
    if (!accessToken) return;
    const profile = await this.restService.fetchAuthMe(accessToken);
    if (!profile?.sessionId) {
      this.emailPasswordError = 'Could not complete sign-in from confirmation link. Please sign in manually.';
      return;
    }
    const refreshToken = params.get('refresh_token');
    const expiresInRaw = params.get('expires_in');
    const expiresAtRaw = params.get('expires_at');
    let expiresAt: number | null = null;
    if (expiresAtRaw && /^\d+$/.test(expiresAtRaw)) {
      expiresAt = Number(expiresAtRaw) * 1000;
    } else if (expiresInRaw && /^\d+$/.test(expiresInRaw)) {
      expiresAt = Date.now() + Number(expiresInRaw) * 1000;
    }
    await this.authService.persistOidcLogin(profile, accessToken, {
      provider: 'supabase-oauth',
      refreshToken: refreshToken && refreshToken.length > 0 ? refreshToken : null,
      expiresAt,
    });
    try {
      win.history.replaceState(null, '', win.location.pathname);
    } catch {
      /* ignore */
    }
    await this.navigateAfterLogin(accessToken, !!profile.admin);
  }

  private async navigateAfterLogin(sessionId: string, admin: boolean): Promise<void> {
    const userId = this.authService.getUser()?.id;
    if (!this.connectivity.isOnline) {
      const accepted = !!userId
        && await this.policyStorage.isCurrent(userId, TERMS_VERSION, PRIVACY_VERSION);
      await this.router.navigate(
        [accepted ? '/dashboard' : '/accept-terms'],
        { replaceUrl: true },
      );
      return;
    }
    try {
      const terms = await this.restService.getCurrentTermsStatus(sessionId);
      if (!terms.accepted) {
        await this.router.navigate(['/accept-terms'], { replaceUrl: true });
        return;
      }
      if (userId) {
        await this.policyStorage.save({
          userId,
          termsVersion: terms.policyVersion || TERMS_VERSION,
          privacyVersion: PRIVACY_VERSION,
          acceptedAt: new Date().toISOString(),
        });
      }
    } catch (error) {
      LoggerService.error('Could not verify terms acceptance', error);
      if (isTermsEndpointMissing(error)) {
        // Older/production APIs without /privacy/terms must not trap signed-in users.
        LoggerService.log('Terms API missing; skipping acceptance gate');
      } else {
        await this.router.navigate(['/accept-terms'], { replaceUrl: true });
        return;
      }
    }
    if (admin && !isCapacitorNative()) {
      await this.router.navigate(['/admin'], { replaceUrl: true });
      return;
    }
    const config = await this.restService.getContactConfig(sessionId);
    const unreadCount = config?.unreadCount ?? 0;
    if (unreadCount > 0) {
      this.messagesNotification.setPendingLoginDialog(unreadCount);
    }
    await this.router.navigate(['/dashboard'], { replaceUrl: true });
  }

  /**
   * Supabase OAuth: pass `provider` on the authorize URL (PKCE code flow).
   * @see https://supabase.com/docs/guides/auth/social-login
   */
  signInWithSocial(provider: 'google' | 'apple'): void {
    this.oidcClickError = '';
    if (!this.runtime.oidcConfigured) {
      return;
    }
    if (!oidcDiscoveryState.loaded) {
      this.oidcClickError =
        oidcDiscoveryState.errorMessage ||
        'Sign-in is not ready. OpenID discovery did not complete.';
      return;
    }
    this.oauth.initCodeFlow('', { provider });
  }

  async retryOidcDiscovery(): Promise<void> {
    if (this.oidcDiscoveryRetrying || !this.runtime.oidcConfigured) {
      return;
    }
    this.oidcDiscoveryRetrying = true;
    this.oidcClickError = '';
    try {
      await setupOidcDiscovery(this.runtime, this.oauth);
    } finally {
      this.oidcDiscoveryRetrying = false;
    }
  }

  get showEmergency(): boolean {
    return !isCapacitorNative() && this.runtime.emergencyLoginVisible;
  }

  async onEmergencySubmit(): Promise<void> {
    LoggerService.log('[Login] emergency submit tapped');
    const username = String(this.emergencyForm.value.username ?? '').trim();
    const password = String(this.emergencyForm.value.password ?? '');
    if (username.length < 4 || password.length < 4) {
      this.emergencyFailed = true;
      return;
    }
    this.emergencyFailed = false;
    const isAuthenticated = await this.authService.authenticate(username, password);
    if (!isAuthenticated) {
      this.emergencyFailed = true;
      return;
    }
    const user = this.authService.getUser();
    if (user?.admin) {
      await this.router.navigate(['/admin']);
      return;
    }
    const config = await this.restService.getContactConfig(user?.sid ?? '');
    const unreadCount = config?.unreadCount ?? 0;
    if (unreadCount > 0) {
      this.messagesNotification.setPendingLoginDialog(unreadCount);
    }
    await this.router.navigate(['/dashboard']);
  }
}
